import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {environment} from '../../../environments/environment';
import {firstValueFrom} from 'rxjs';
import {IRecordSleep} from './record-sleep.interface';

export interface IRSleep {
  recordSleep: IRecordSleep;
  wasDeployed?: boolean;
}

export namespace RecordSleepAction {
  export class GetActive {
    static readonly type = '[Record] Get Sleep Action';
    constructor(public serviceId: string, public patientId: string) { }
  }

  export class Post {
    static readonly type = '[Record] Post Sleep Action';
    constructor(public serviceId: string, public patientId: string, public recordSleep: Partial<IRecordSleep>) { }
  }

  export class PatchActive {
    static readonly type = '[Record] Patch Sleep Action';
    constructor(public serviceId: string, public patientId: string, public sleepId: string, public recordSleep: Partial<IRecordSleep>) { }
  }

  export class ResetState {
    static readonly type = '[Record] Reset Sleep Action';
    constructor() { }
  }

  export class Deploy {
    static readonly type = '[Record] Was Deployed Sleep Action';
    constructor(public value: boolean) { }
  }
}

@State<IRSleep>({
  name: 'SleepState',
  defaults: {
    recordSleep: null
  },
})

@Injectable()
export class SleepState {
  constructor(
    private baseService: BaseService<any>,
  ) {
  }

  url = environment.SERVER_URL;

  @Selector()
  public static getSleep(state: IRSleep): IRecordSleep {
    return state.recordSleep;
  }

  @Selector()
  public static getWasDeployed(state: IRSleep): boolean {
    return state.wasDeployed;
  }

  @Action(RecordSleepAction.GetActive)
  async getSleepAction(ctx: StateContext<IRSleep>, action: RecordSleepAction.GetActive): Promise<void> {
    const response: DataBaseServiceResponse<IRecordSleep> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/sleep/active`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      recordSleep: response.entity
    });
  }

  @Action(RecordSleepAction.Post)
  async postSleepAction(ctx: StateContext<IRSleep>, {recordSleep, serviceId, patientId}: RecordSleepAction.Post): Promise<void> {
    const response: DataBaseServiceResponse<IRecordSleep> = await firstValueFrom(
      this.baseService.post(`${this.url}/patients/${patientId}/service/${serviceId}/records/sleep`, recordSleep)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      recordSleep: response.entity
    });
  }

  @Action(RecordSleepAction.PatchActive)
  async patchSleepAction(ctx: StateContext<IRSleep>, {recordSleep, patientId, serviceId}: RecordSleepAction.PatchActive): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/patients/${patientId}/service/${serviceId}/records/sleep`, recordSleep)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordSleepAction.ResetState)
  async resetSleepAction(ctx: StateContext<IRSleep>, _: RecordSleepAction.ResetState): Promise<void> {
    ctx.patchState({
      recordSleep: null
    });
  }

  @Action(RecordSleepAction.Deploy)
  async wasDeployedPhysicalActivityAction(ctx: StateContext<IRSleep>, action: RecordSleepAction.Deploy): Promise<void> {
    ctx.patchState({
      wasDeployed: action.value
    });
  }
}
